import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Checks, CircleNotch, BellSlash, User, SignIn, SignOut, ShieldCheck,
  UsersThree, Megaphone, Wrench, FileText, ChatCircleDots, CalendarBlank, ChartBar,
  Warning, Wallet, CheckSquare, Bell,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

const CY = "#00E5FF", LI = "#CCFF00", PK = "#FF2A6D", AM = "#FFB800", SOFT = "rgba(255,255,255,0.6)";
// Per-kind icon + accent so the feed reads at a glance (MyGate-style).
const KIND_META = {
  VISITOR_APPROVAL: { Icon: User, c: CY }, VISITOR_ENTRY: { Icon: SignIn, c: CY },
  VISITOR_EXIT: { Icon: SignOut, c: SOFT }, VISITOR_DECISION: { Icon: ShieldCheck, c: LI },
  HELP_ATTENDANCE: { Icon: UsersThree, c: CY }, ANNOUNCEMENT: { Icon: Megaphone, c: AM },
  COMPLAINT_RAISED: { Icon: Wrench, c: LI }, COMPLAINT_STATUS: { Icon: FileText, c: LI },
  CHAT_MESSAGE: { Icon: ChatCircleDots, c: CY }, CHAT_URGENT: { Icon: ChatCircleDots, c: PK },
  BOOKING_REQUEST: { Icon: CalendarBlank, c: LI }, BOOKING_CONFIRMED: { Icon: CalendarBlank, c: LI },
  BOOKING_DECISION: { Icon: CalendarBlank, c: CY }, POLL_NEW: { Icon: ChartBar, c: AM },
  SOS_ALERT: { Icon: Warning, c: PK }, SOS_RESOLVED: { Icon: ShieldCheck, c: LI },
  CASHBOOK_FLAG: { Icon: Wallet, c: AM }, TASK_CREATED: { Icon: CheckSquare, c: LI },
};
const metaFor = (k) => KIND_META[k] || { Icon: Bell, c: CY };

function timeAgo(iso) {
  const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString("en-IN");
}
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const IST = 19800000;
const istKey = (ms) => new Date(ms + IST).toISOString().slice(0, 10);
function dayLabel(iso) {
  try {
    const key = istKey(new Date(iso).getTime());
    if (key === istKey(Date.now())) return "Today";
    if (key === istKey(Date.now() - 86400000)) return "Yesterday";
    const [, mm, dd] = key.split("-");
    return `${dd} ${MON[+mm - 1]}`;
  } catch { return "Earlier"; }
}
function groupByDay(items) {
  const out = []; let cur = null;
  for (const n of items) {
    const label = dayLabel(n.createdAt);
    if (!cur || cur.label !== label) { cur = { label, rows: [] }; out.push(cur); }
    cur.rows.push(n);
  }
  return out;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/api/notifications?limit=80"); setItems(data.items || []); setUnread(data.unread || 0); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function openItem(n) {
    if (!n.readAt) { try { await api.patch(`/api/notifications/${n.id}/read`); } catch {} setUnread((u) => Math.max(0, u - 1)); setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, readAt: "read" } : x))); }
    if (n.link && n.link !== "/") navigate(n.link);
  }
  async function markAll() { try { await api.post("/api/notifications/read-all"); } catch {} setUnread(0); setItems((arr) => arr.map((x) => ({ ...x, readAt: x.readAt || "read" }))); }

  const groups = groupByDay(items);

  return (
    <div className="space-y-4 font-sans">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white/70 hover:text-white">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        {unread > 0 && (
          <button onClick={markAll} className="inline-flex items-center gap-1 rounded-full border border-neon-cyan/30 bg-neon-cyan/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-neon-cyan hover:bg-neon-cyan/20">
            <Checks size={13} weight="bold" /> Mark all read
          </button>
        )}
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Notifications</p>
        <h1 className="font-display text-3xl font-extrabold text-white">{unread > 0 ? `${unread} unread` : "All caught up"}</h1>
      </div>

      {loading && items.length === 0 ? (
        <div className="card flex items-center justify-center p-10 text-white/55"><CircleNotch className="animate-spin" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 text-white/55"><BellSlash size={22} weight="duotone" /></div>
          <p className="text-[13px] text-white/55">No notifications yet.</p>
          <p className="text-[12px] text-white/40">Visitor alerts, approvals and notices will appear here.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.label} className="space-y-2">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/40">{g.label}</p>
              <div className="grid gap-2 md:grid-cols-2">
                {g.rows.map((n, i) => {
                  const { Icon, c } = metaFor(n.kind);
                  const unreadRow = !n.readAt;
                  return (
                    <motion.button key={n.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.03 }}
                      onClick={() => openItem(n)}
                      className="flex w-full items-start gap-3 rounded-2xl border p-3.5 text-left transition hover:brightness-110"
                      style={{ borderColor: unreadRow ? `${c}55` : "rgba(255,255,255,0.10)", background: unreadRow ? `${c}12` : "rgba(255,255,255,0.03)" }}>
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${c}22` }}>
                        <Icon size={20} weight="fill" color={c} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`flex-1 truncate text-[14px] ${unreadRow ? "font-bold text-white" : "font-semibold text-white/80"}`}>{n.title}</p>
                          {n.urgent && <span className="rounded bg-neon-pink/15 px-1.5 py-0.5 text-[9px] font-extrabold tracking-wide text-neon-pink">URGENT</span>}
                          {unreadRow && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c }} />}
                        </div>
                        {n.body && <p className="mt-0.5 line-clamp-2 text-[12.5px] leading-relaxed text-white/60">{n.body}</p>}
                        <p className="mt-1 text-[10.5px] text-white/40">{timeAgo(n.createdAt)} ago</p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
